import cloneDeep from 'lodash/cloneDeep';
import type {
  MutationObserverOptions,
  UseQueryReturnType,
  UseMutationReturnType,
  QueryObserverOptions,
} from '@tanstack/vue-query';
import type { MaybeRefOrGetter } from 'vue';

type QueryOptions = {
  enabled?: MaybeRefOrGetter<QueryObserverOptions['enabled']>;
  refetchInterval?: number | false | (() => number | false);
  staleTime?: number;
  placeholderData?: any;
  retry?: number;
  retryDelay?: number;
};

/**
 * This function is a helper to create a v-model for a query and a mutation.
 *
 * It can only be used when the query and the mutation have the same data type.
 *
 * Note that it doesn't handle any optimistic updating or invalidation logic:
 * that would best be done in the composables themselves.
 */
export default function useQueryVModelFactory<
  TData = unknown,
  TQueryError = unknown,
  TUpdateError = unknown,
  TUpdateContext = unknown,
>(
  useQueryComposable: (options?: QueryOptions) => UseQueryReturnType<TData, TQueryError>,
  useUpdateComposable: (
    options?: MutationObserverOptions<TData, TUpdateError, TData, TUpdateContext>,
  ) => UseMutationReturnType<TData, TUpdateError, TData, TUpdateContext>,
) {
  return (options?: {
    queryOptions?: QueryOptions;
    mutationOptions?: MutationObserverOptions<TData, TUpdateError, TData, TUpdateContext>;
  }) => {
    const query = useQueryComposable(options?.queryOptions);
    const mutation = useUpdateComposable(options?.mutationOptions);

    const data = ref(cloneDeep(query.data.value)) as Ref<TData | undefined>;

    const fromQuery = ref(false);
    watch(
      query.data,
      (newValue) => {
        fromQuery.value = true;

        // We have to clone the data to avoid mutating the query data
        data.value = cloneDeep(newValue);

        // This is to ensure that the query itself doesn't cause the mutation to fire
        void nextTick(() => {
          fromQuery.value = false;
        });
      },
      { deep: true },
    );

    watch(
      data,
      (newValue) => {
        if (fromQuery.value) return;
        if (newValue) mutation.mutate(newValue);
      },
      { deep: true },
    );

    return {
      data,
      query,
      mutation,
      isLoading: computed(() => query.isLoading.value || mutation.isPending.value),
      isError: computed(() => query.isError.value || mutation.isError.value),
    };
  };
}
